import { Table } from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import { RootState } from 'client/store/configureStore';
import { getQueryAuditLog } from 'client/store/entities/query/query';
import { getCompanyUsers } from 'client/store/entities/user/selectors';
import { getUsers } from 'client/store/entities/user/user';
import { IQuery, IQueryTemplateAuditEntry } from 'common/interfaces/query';
import { DateService } from 'common/services/date-service';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { QueryChangeDetailsModal } from './query-change-details-modal';
import {
  getAuditUserName, getChangedFields, QUERY_AUDIT_ACTION_LABEL_MAP, QUERY_AUDIT_FIELD_LABEL_MAP,
} from './query-changes-utils';

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

interface IQueryChangesProps {
  // The template as last saved; a new reference after each save triggers a reload
  savedQuery: IQuery;
}

export const QueryChanges = ({ savedQuery }: IQueryChangesProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { entries, isLoading } = useSelector(({ entities }: RootState) => entities.query.auditLog);
  const users = useSelector(getCompanyUsers);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [selectedEntry, setSelectedEntry] = useState<IQueryTemplateAuditEntry>();

  useEffect(() => {
    dispatch(getQueryAuditLog(savedQuery.queryid!));
  }, [dispatch, savedQuery]);

  const hasUsers = !!users?.length;
  useEffect(() => {
    if (!hasUsers) {
      dispatch(getUsers());
    }
  }, [dispatch, hasUsers]);

  const toChangeCell = (entry: IQueryTemplateAuditEntry) => {
    const actionLabel = t(QUERY_AUDIT_ACTION_LABEL_MAP[entry.action]);
    const changedFields = getChangedFields(entry);
    if (!changedFields.length) {
      return { value: actionLabel };
    }
    return {
      type: 'Link',
      value: `${actionLabel}: ${changedFields.map(field => t(QUERY_AUDIT_FIELD_LABEL_MAP[field])).join(', ')}`,
      onClick: () => setSelectedEntry(entry),
    };
  };

  const pageEntries = entries.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const table = {
    name: t(translations.query_changes),
    headers: [{
      cells: [
        { value: t(translations.query_change) },
        { value: t(translations.query_modifiedBy) },
        { value: t(translations.query_date) },
      ],
    }],
    rows: pageEntries.length
      ? pageEntries.map(entry => ({
        cells: [
          toChangeCell(entry),
          { value: getAuditUserName(users, entry.subjectid) },
          { value: DateService.format(entry.created) },
        ],
      }))
      : [{
        cells: [{
          value: isLoading ? t(translations.hierarchy_loading) : t(translations.query_historyEmpty),
          colSpan: 3,
        }],
      }],
    footer: {
      pagination: {
        rowCount: entries.length,
        selectedPage: page,
        rowsPerPage: {
          onChange: (event: { target: { value: string } }) => {
            setRowsPerPage(Number(event.target.value));
            setPage(1);
          },
          options: ROWS_PER_PAGE_OPTIONS.map(value => ({ label: `${value} / ${t(translations.page)}`, value })),
          value: rowsPerPage,
        },
        onSelectPage: setPage,
        small: true,
      },
    },
  };

  return (
    <>
      <Table table={table} />
      {selectedEntry && (
        <QueryChangeDetailsModal entry={selectedEntry} onClose={() => setSelectedEntry(undefined)} />
      )}
    </>
  );
};
